import {
  type ContactList,
  type ContactListItem,
  type ContactSelection,
  formatPhone,
} from "@kontaktfix/shared";
import { Avatar, Button, Checkbox, Skeleton, Table, Td, Th, Tooltip, Tr } from "@kontaktfix/ui";
import { CaretLeftIcon, CaretRightIcon, LockIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { ListBadge } from "@/components/list-badge";
import { formatNumber, formatRelative } from "@/lib/i18n";

/**
 * Rows picked by hand, or with `all` every contact matching the filter except `ids`, the rows
 * unchecked again (issue #56). Toggling a row flips its id in both modes.
 */
export type Selection = { all: boolean; ids: Set<string> };
export const noSelection: Selection = { all: false, ids: new Set() };
export const isSelected = (s: Selection, id: string) => s.all !== s.ids.has(id);
export const selectionCount = (s: Selection, total: number) => (s.all ? total - s.ids.size : s.ids.size);

/** The request body for a bulk action; the server resolves "all" with the same filter. */
export function selectionBody(
  s: Selection,
  filter: { q?: string; listId?: string },
  total: number,
): ContactSelection {
  return s.all
    ? {
        filter: { q: filter.q || undefined, listId: filter.listId },
        exceptIds: [...s.ids],
        expectedCount: selectionCount(s, total),
      }
    : { contactIds: [...s.ids] };
}

/** Gmail-style: once the whole page is checked, offer all matching contacts, and a way back. */
export function SelectAllBanner({
  items,
  total,
  selected,
  onSelectedChange,
}: {
  items: ContactListItem[];
  total: number;
  selected: Selection;
  onSelectedChange: (next: Selection) => void;
}) {
  const { t } = useTranslation();
  const pageSelected = items.length > 0 && items.every((i) => isSelected(selected, i.id));
  if (!selected.all && !(pageSelected && total > items.length)) return null;
  const count = selectionCount(selected, total);
  return (
    <div className="animate-fade-in flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-b border-border bg-subtle px-4 py-2 text-[12.5px] text-fg-muted">
      {selected.all ? (
        <>
          <span>
            {selected.ids.size
              ? t("contacts.allSelectedExcept", { n: formatNumber(count), total: formatNumber(total) })
              : t("contacts.allSelected", { n: formatNumber(total) })}
          </span>
          <button
            type="button"
            className="font-medium text-accent hover:underline"
            onClick={() => onSelectedChange(noSelection)}
          >
            {t("contacts.clearSelection")}
          </button>
        </>
      ) : (
        <>
          <span>{t("contacts.pageSelected", { n: formatNumber(items.length) })}</span>
          <button
            type="button"
            className="font-medium text-accent hover:underline"
            onClick={() => onSelectedChange({ all: true, ids: new Set() })}
          >
            {t("contacts.selectAll", { n: formatNumber(total) })}
          </button>
        </>
      )}
    </div>
  );
}

export function ContactsTable({
  items,
  lists,
  loading,
  selected,
  onSelectedChange,
  onOpen,
  selectable = true,
}: {
  items: ContactListItem[];
  lists: ContactList[];
  loading: boolean;
  selected: Selection;
  onSelectedChange: (next: Selection) => void;
  onOpen: (id: string) => void;
  selectable?: boolean;
}) {
  const { t } = useTranslation();
  const listById = new Map(lists.map((l) => [l.id, l]));
  const allSelected = items.length > 0 && items.every((i) => isSelected(selected, i.id));
  const someSelected = items.some((i) => isSelected(selected, i.id));

  const toggle = (id: string) => {
    const next = new Set(selected.ids);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedChange({ all: selected.all, ids: next });
  };
  const toggleAll = () => {
    const next = new Set(selected.ids);
    for (const i of items) {
      // Check the page unless it is fully checked; in "all" mode an id in the set means unchecked.
      if (allSelected === selected.all) next.add(i.id);
      else next.delete(i.id);
    }
    onSelectedChange({ all: selected.all, ids: next });
  };

  return (
    <Table>
      <thead>
        <tr>
          {selectable && (
            <Th className="w-10">
              <Checkbox
                checked={allSelected ? true : someSelected ? "indeterminate" : false}
                onCheckedChange={toggleAll}
                label="All"
              />
            </Th>
          )}
          <Th>{t("contacts.columns.name")}</Th>
          <Th className="hidden md:table-cell">{t("contacts.columns.phone")}</Th>
          <Th className="hidden xl:table-cell">{t("contacts.columns.email")}</Th>
          <Th className="hidden sm:table-cell">{t("contacts.columns.lists")}</Th>
          <Th className="hidden text-right lg:table-cell">{t("contacts.columns.updated")}</Th>
        </tr>
      </thead>
      <tbody>
        {loading && !items.length
          ? Array.from({ length: 6 }, (_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
              <Tr key={i}>
                <Td colSpan={6}>
                  <Skeleton className="h-5 w-full" />
                </Td>
              </Tr>
            ))
          : items.map((c) => (
              <Tr
                key={c.id}
                onClick={() => onOpen(c.id)}
                className={isSelected(selected, c.id) ? "bg-accent-soft/40" : undefined}
              >
                {selectable && (
                  <Td className="w-10">
                    <Checkbox
                      checked={isSelected(selected, c.id)}
                      onCheckedChange={() => toggle(c.id)}
                      label={c.displayName}
                    />
                  </Td>
                )}
                <Td>
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={c.displayName}
                      src={c.hasPhoto ? `/api/contacts/${c.id}/photo?v=${c.photoVersion}` : null}
                      size={28}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 font-medium">
                        <span className="truncate">{c.displayName}</span>
                        {!c.canEdit && (
                          <Tooltip content={t("contacts.readOnlyHint")}>
                            <LockIcon className="size-3 text-fg-subtle" />
                          </Tooltip>
                        )}
                      </div>
                      <div className="truncate text-[12px] text-fg-muted">
                        {[c.jobTitle, c.companyName !== c.displayName ? c.companyName : null]
                          .filter(Boolean)
                          .join(" · ") || " "}
                      </div>
                    </div>
                  </div>
                </Td>
                <Td className="hidden font-mono text-[12.5px] whitespace-nowrap text-fg-muted md:table-cell">
                  {c.primaryPhone ? formatPhone(c.primaryPhone) : ""}
                </Td>
                <Td className="hidden max-w-56 truncate text-fg-muted xl:table-cell">{c.email1}</Td>
                <Td className="hidden sm:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {c.listIds.slice(0, 2).map((id) => {
                      const list = listById.get(id);
                      return list ? <ListBadge key={id} name={list.name} color={list.color} /> : null;
                    })}
                    {c.listIds.length > 2 && (
                      <span className="text-[12px] text-fg-subtle">+{c.listIds.length - 2}</span>
                    )}
                  </div>
                </Td>
                <Td className="hidden text-right text-[12.5px] whitespace-nowrap text-fg-subtle lg:table-cell">
                  {formatRelative(c.updatedAt)}
                </Td>
              </Tr>
            ))}
      </tbody>
    </Table>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[12.5px] text-fg-muted">
      <span>{t("common.page", { from, to, total })}</span>
      <div className="flex gap-1">
        <Button
          size="icon-sm"
          variant="ghost"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          aria-label={t("common.previous")}
        >
          <CaretLeftIcon />
        </Button>
        <Button
          size="icon-sm"
          variant="ghost"
          disabled={to >= total}
          onClick={() => onPage(page + 1)}
          aria-label={t("common.next")}
        >
          <CaretRightIcon />
        </Button>
      </div>
    </div>
  );
}
